import React, { useEffect, useState } from 'react';
import { DatePickerComponent, ChangeEventArgs } from '@syncfusion/ej2-react-calendars';
import { TooltipComponent } from '@syncfusion/ej2-react-popups';
import { Button, Modal } from 'react-bootstrap';
import get from 'lodash/get';
import { useFormContext } from 'react-hook-form';

interface IInputProps {
  /** Nombre con el que el campo será registrado en react-hook-form */
  name: string;
  /** Identificador HTML del elemento */
  id: string;
  /** Etiqueta visible encima del control */
  label?: string;
  /** Placeholder del DatePicker */
  placeholder?: string;
  /** Menú contextual con información */
  modal?: string;

  /** Año mínimo permitido */
  minDate?: Date;
  /** Año máximo permitido */
  maxDate?: Date;
  /** Marca el campo como requerido con el mensaje indicado */
  req?: any;
  /** Deshabilita totalmente la selección */
  disableDate?: boolean;
  /** Callback opcional que recibe el Date completo para, por ejemplo, obtener el día de la semana */
  getDayWeek?: (date: Date) => void;

  /** Columnas Bootstrap */
  xl?: number;
  md?: number;
  col?: number;

  /** Año por defecto. Si no se indica comienza vacío */
  defaultValue?: number;
}

/**
 * Componente selector de *año* que integra **Syncfusion DatePicker** con **react-hook-form**.
 * Almacenará en el formulario un **número (int)** representando solo el año seleccionado.
 */
const FormInputYear: React.FC<IInputProps> = ({
  name,
  id,
  label,
  placeholder,
  getDayWeek,
  minDate,
  maxDate,
  req,
  disableDate,
  modal,
  xl = 12,
  md = 12,
  col = 12,
  defaultValue
}) => {
  /* -------------------------------------------------------------------------- */
  /* React-Hook-Form                                                            */
  /* -------------------------------------------------------------------------- */
  const {
    register,
    setValue,
    formState: { errors }
  } = useFormContext();

  /* -------------------------------------------------------------------------- */
  /* Estado local: solo guardamos el año (int)                                   */
  /* -------------------------------------------------------------------------- */
  const [selectedYear, setSelectedYear] = useState<number | null>();

  useEffect(() => {
    if (typeof defaultValue === 'number') {
      setSelectedYear(defaultValue);
      setValue(name, defaultValue); // Asegura el formState también
    }
  }, [defaultValue]);

  /** Maneja la selección del año */
  const handleDateChange = ({ value }: ChangeEventArgs) => {
    const date: Date | null = value as Date | null;
    const year = date ? date.getFullYear() : null;

    setSelectedYear(year);
    // Actualizamos el valor del formulario con un entero (o string si prefieres)
    setValue(name, year, { shouldValidate: true });

    // Callback opcional con la fecha completa
    if (getDayWeek && date) getDayWeek(date);
  };

  /* -------------------------------------------------------------------------- */
  /* Modal información                                                          */
  /* -------------------------------------------------------------------------- */
  const [show, setShow] = useState(false);
  const handleShow = () => setShow(true);
  const handleClose = () => setShow(false);

  /* -------------------------------------------------------------------------- */
  /* Render                                                                     */
  /* -------------------------------------------------------------------------- */
  const errorMessage = get(errors, name)?.message as string | undefined;

  return (
    <div className={`col-xl-${xl} col-md-${md} col-${col}`}>
      <div className={`input-block ${id} ${errorMessage ? 'error' : ''}`}>
        {label && (
          <label htmlFor={id} className="input-block__label">
            {label}
          </label>
        )}

        {/* Icono información opcional */}
        {modal && (
          <TooltipComponent content="Más información">
            <div onClick={handleShow} className="information-icon-orange p-0" />
          </TooltipComponent>
        )}

        {/* DatePicker visual (siempre trabaja con Date) */}
        <DatePickerComponent
          id={id}
          format="yyyy"
          firstDayOfWeek={1}
          start="Decade"
          depth="Decade"
          disabled={disableDate}
          min={minDate}
          max={maxDate}
          value={selectedYear !== null ? new Date(selectedYear, 0, 1) : null}
          change={handleDateChange}
          placeholder={placeholder}
        />

        {/* Input oculto que almacena el año como entero para react-hook-form */}
        <input
          type="hidden"
          {...register(name, { required: req })}
          value={selectedYear ?? ''}
        />

        {errorMessage && <p className="error m-0">{errorMessage}</p>}

        {/* Modal informativo */}
        <Modal show={show} onHide={handleClose}>
          <Modal.Header closeButton>
            <Modal.Title>{label}</Modal.Title>
          </Modal.Header>
          <Modal.Body>{modal}</Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default FormInputYear;
